import { createSlice } from '@reduxjs/toolkit';
import {
  fetchEcommerceIntegrations,
  fetchEcommerceWarehouses,
  createEcommerceIntegration,
  updateEcommerceIntegration,
  deleteEcommerceIntegration,
  initMeliAuth,
  initShopifyAuth,
  retryMeliAuth,
} from './ecommercesThunks';

/**
 * The state the legacy `useEcommerceIntegrations` hook kept in `useState`, plus
 * what the listing and the detail page share.
 *
 * One `loading` and one `error` for every action, as in the hook. Two details
 * are kept on purpose: the warehouses never touch them (they came from a
 * separate hook), and a failed Shopify connection does not set `error` either —
 * the form shows that one itself.
 */
export const initialFilters = {
  search: '',
  /** A slug from `PLATFORMS`. */
  platform: '',
  warehouse: '',
  page: 1,
  rowsPerPage: 20,
};

const initialState = {
  integrations: [],
  warehouses: [],
  /** Logo URLs by key, for the catalog of integrations to add. */
  logos: {},
  loading: false,
  error: null,
  /** Kept in the store rather than the page, so the filters survive a visit to a detail. */
  filters: initialFilters,
};

const startLoading = (state) => {
  state.loading = true;
  state.error = null;
};

const stopLoading = (state) => {
  state.loading = false;
};

const failWithError = (state, action) => {
  state.loading = false;
  state.error = action.payload;
};

const ecommercesSlice = createSlice({
  name: 'ecommerces',
  initialState,
  reducers: {
    /** Any change other than the page itself goes back to the first page. */
    setEcommerceFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEcommerceIntegrations.pending, startLoading)
      .addCase(fetchEcommerceIntegrations.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = action.payload.integrations;
        state.logos = action.payload.logos;
      })
      .addCase(fetchEcommerceIntegrations.rejected, failWithError)

      .addCase(fetchEcommerceWarehouses.fulfilled, (state, action) => {
        state.warehouses = action.payload;
      })

      .addCase(createEcommerceIntegration.pending, startLoading)
      .addCase(createEcommerceIntegration.fulfilled, stopLoading)
      .addCase(createEcommerceIntegration.rejected, failWithError)

      .addCase(updateEcommerceIntegration.pending, startLoading)
      .addCase(updateEcommerceIntegration.fulfilled, stopLoading)
      .addCase(updateEcommerceIntegration.rejected, failWithError)

      .addCase(deleteEcommerceIntegration.pending, startLoading)
      .addCase(deleteEcommerceIntegration.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = state.integrations.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteEcommerceIntegration.rejected, failWithError)

      .addCase(initMeliAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initMeliAuth.fulfilled, stopLoading)
      .addCase(initMeliAuth.rejected, failWithError)

      .addCase(initShopifyAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initShopifyAuth.fulfilled, stopLoading)
      .addCase(initShopifyAuth.rejected, stopLoading)

      // The page reports a failed retry in a toast; `error` stays for the listing.
      .addCase(retryMeliAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(retryMeliAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.integrations = state.integrations.filter(
          (item) => item.id !== action.payload.removedId
        );
      })
      .addCase(retryMeliAuth.rejected, stopLoading);
  },
});

export const { setEcommerceFilters } = ecommercesSlice.actions;

export default ecommercesSlice.reducer;
